import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Send,
  Component as ComponentIcon,
  MonitorPlay,
  Sparkles,
  AlertTriangle,
  ExternalLink,
  PanelBottom,
  RefreshCw,
} from 'lucide-react';
import { api } from '../hooks/api.js';
import { useEditorStore, useEditorHistory } from '../stores/editorStore.js';
import { useAiComposer } from '../stores/aiComposer.js';
import { AiComposer } from '../components/ai/AiComposer.js';
import { EditorToolbar } from '../components/editor/EditorToolbar.js';
import { EditorCanvas } from '../components/editor/EditorCanvas.js';
import { InspectorPanel } from '../components/InspectorPanel.js';
import { PanelResizer } from '../components/PanelResizer.js';
import { SaveComponentModal } from '../components/SaveComponentModal.js';
import { PreviewModal } from '../components/PreviewModal.js';
import { PaneLoading } from '../components/PaneLoading.js';
import { useResizablePanel } from '../hooks/use-resizable-panel.js';
import { useEditorFonts } from '../hooks/useEditorFonts.js';
import { useEditorShortcuts } from '../hooks/useEditorShortcuts.js';
import { useEditorAutosave } from '../hooks/useEditorAutosave.js';
import { SendTestEmailModal } from '../components/SendTestEmailModal.js';
import { useDocumentDesign } from '../hooks/useDocumentDesign.js';
import { useAuthStore } from '../stores/authStore.js';
import {
  footerReferencesUnsubscribe,
  type Block,
  type ComponentUsage,
  type EmailFooterOverride,
} from '@blogforge/shared';

/** Path of the workspace email-footer editor (the shared default). */
const WORKSPACE_FOOTER_EDITOR_PATH = '/settings/email-footer';

/** Footer modes, in the same segmented-control shape as the components list. */
const FOOTER_MODES = [
  { value: 'inherit', label: 'Workspace footer' },
  { value: 'custom', label: 'Custom footer for this email' },
] as const;

/**
 * Read a stored per-email footer override. The column is jsonb, but every read
 * in this app tolerates the JSON-string form too — and anything unreadable
 * degrades to `null`, which means "inherit the workspace footer".
 */
function parseFooterOverride(value: unknown): EmailFooterOverride | null {
  let candidate = value;
  if (typeof candidate === 'string') {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return null;
    }
  }
  if (!candidate || typeof candidate !== 'object') return null;
  const blocks = (candidate as { blocks?: unknown }).blocks;
  return Array.isArray(blocks) ? { blocks: blocks as Block[] } : null;
}

/** The workspace footer's authored blocks, however site settings store them. */
function parseWorkspaceFooterBlocks(site: unknown): Block[] {
  let footer = (site as { emailFooter?: unknown } | undefined)?.emailFooter;
  if (typeof footer === 'string') {
    try {
      footer = JSON.parse(footer);
    } catch {
      return [];
    }
  }
  const blocks = (footer as { blocks?: unknown } | null | undefined)?.blocks;
  return Array.isArray(blocks) ? (blocks as Block[]) : [];
}

interface EmailFooterPanelProps {
  emailId: string;
  email: unknown;
  /** Sent emails have already shipped their footer — nothing to change. */
  readOnly: boolean;
}

/**
 * Per-email footer: inherit the workspace footer, or send custom content.
 *
 * An override replaces the footer's *content* only — the workspace footer still
 * supplies the details (logo, socials, address, legal, tagline) its
 * `{{system variables}}` read, so a custom footer stays cheap to keep current.
 */
function EmailFooterPanel({ emailId, email, readOnly }: EmailFooterPanelProps) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);

  // The email row in the react-query cache is the single source of truth: the
  // page's autosave never sends `footerOverride`, so patching the cache keeps
  // this panel in step without refetching (a refetch would reset the editor's
  // in-flight blocks).
  const emailKey = ['email', emailId] as const;
  const raw = (email as Record<string, unknown> | undefined)?.['footerOverride'];
  const override = parseFooterOverride(raw);
  const isCustom = override !== null;
  const customBlocks = override?.blocks ?? [];

  const write = useMutation({
    mutationFn: (next: EmailFooterOverride | null) =>
      api.emails.update(emailId, { footerOverride: next }),
  });

  const setOverride = (next: EmailFooterOverride | null) => {
    qc.setQueryData(emailKey, (prev) =>
      prev && typeof prev === 'object'
        ? { ...(prev as Record<string, unknown>), footerOverride: next }
        : prev,
    );
  };

  // Optimistic: the toggle should read instantly, and the request only ever
  // carries `footerOverride`, so an omitted field cannot disturb the email.
  const apply = (next: EmailFooterOverride | null) => {
    const previous = override;
    setOverride(next);
    write.mutate(next, { onError: () => setOverride(previous) });
  };

  // Seed a new override from the workspace footer's blocks so the author starts
  // from real content. An unreadable site settings row degrades to an empty
  // override — never a footerless email, since the engine falls back to the
  // workspace footer's details composition while a custom footer has no blocks.
  const seedFromWorkspace = async (): Promise<Block[]> => {
    try {
      const site = await qc.fetchQuery({
        queryKey: ['site-settings', activeWorkspaceId],
        queryFn: () => api.settings.getSite(),
        staleTime: 30_000,
      });
      return parseWorkspaceFooterBlocks(site);
    } catch {
      return [];
    }
  };

  const enableCustomFooter = async () => {
    if (isCustom) return;
    apply({ blocks: await seedFromWorkspace() });
  };

  const recopyWorkspaceFooter = async () => {
    apply({ blocks: await seedFromWorkspace() });
  };

  // Custom footers are edited in place for this email; the shared default lives
  // in the workspace footer editor.
  const openFooterEditor = () =>
    navigate(isCustom ? `/emails/${emailId}/footer` : WORKSPACE_FOOTER_EDITOR_PATH);

  const missingUnsubscribe =
    isCustom && customBlocks.length > 0 && !footerReferencesUnsubscribe(customBlocks);

  return (
    <section className="border-line bg-card shrink-0 border-b px-4 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="kicker shrink-0">
          <PanelBottom size={12} />
          Footer
        </span>

        {readOnly ? (
          <span className="chip px-2 py-0.5 text-[10.5px] tracking-wide uppercase">
            {isCustom ? 'Custom · read-only' : 'Workspace · read-only'}
          </span>
        ) : (
          <div className="border-line bg-bg/60 flex items-center gap-1 rounded-full border p-1">
            {FOOTER_MODES.map((mode) => {
              const active = mode.value === 'custom' ? isCustom : !isCustom;
              return (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => {
                    if (mode.value === 'custom') void enableCustomFooter();
                    else if (isCustom) apply(null);
                  }}
                  disabled={write.isPending}
                  title={
                    mode.value === 'custom'
                      ? 'Give this email its own footer content (seeded from the workspace footer)'
                      : 'Inherit the workspace footer — clears any per-email footer content'
                  }
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-150 disabled:opacity-50 ${
                    active ? 'bg-card text-ink shadow-sm' : 'text-muted hover:text-ink-soft'
                  }`}
                >
                  {mode.label}
                </button>
              );
            })}
          </div>
        )}

        {write.isPending && <span className="text-muted text-[11px]">Saving…</span>}
        {write.isError && (
          <span className="text-danger text-[11px]">Couldn’t save the footer — try again.</span>
        )}
      </div>

      <div className="mt-1.5 space-y-1">
        {readOnly ? (
          <p className="text-muted text-[12px] leading-relaxed">
            This email has already been sent, so its footer can no longer be changed — it keeps the
            footer it was built with.
          </p>
        ) : isCustom ? (
          <>
            <p className="text-muted text-[12px] leading-relaxed">
              This email sends its own footer content — authored in the footer editor — instead of
              the workspace footer’s. The workspace footer still supplies the details its{' '}
              <code className="font-mono">{'{{system variables}}'}</code> read: logo, socials,
              address, legal and tagline.
            </p>
            <p className="text-muted text-[12px] leading-relaxed">
              A custom footer must contain an unsubscribe link (
              <code className="font-mono">{'{{unsubscribe_url}}'}</code>) or the build fails.
            </p>
            {customBlocks.length === 0 && (
              <p className="text-muted text-[12px] leading-relaxed">
                No custom content yet — the workspace footer is still rendered (its details
                composition), so this email is never sent without a footer.
              </p>
            )}
            {missingUnsubscribe && (
              <p className="text-danger flex items-start gap-1.5 text-[12px] leading-relaxed">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                <span>
                  This footer has no unsubscribe link, so the build will fail. Add a link or button
                  whose URL is <code className="font-mono">{'{{unsubscribe_url}}'}</code>, or
                  re-copy the workspace footer.
                </span>
              </p>
            )}
            <div className="flex flex-wrap items-center gap-1 pt-0.5">
              <button
                type="button"
                onClick={openFooterEditor}
                className="btn-ghost px-2.5 py-1.5 text-[11px]"
                title="Footer content is authored in the footer editor — this email keeps its own copy of it"
              >
                <ExternalLink size={12} />
                Edit footer
              </button>
              <button
                type="button"
                onClick={() => void recopyWorkspaceFooter()}
                disabled={write.isPending}
                className="btn-ghost px-2.5 py-1.5 text-[11px] disabled:opacity-40"
                title="Replace this email’s footer content with the workspace footer’s current blocks"
              >
                <RefreshCw size={12} />
                Re-copy from workspace footer
              </button>
              <button
                type="button"
                onClick={() => apply(null)}
                disabled={write.isPending}
                className="btn-ghost px-2.5 py-1.5 text-[11px] disabled:opacity-40"
                title="Clear this email’s footer content and inherit the workspace footer again"
              >
                Use workspace footer
              </button>
            </div>
          </>
        ) : (
          <p className="text-muted text-[12px] leading-relaxed">
            Inherits the workspace footer.{' '}
            <button
              type="button"
              onClick={openFooterEditor}
              className="text-accent hover:underline"
              title="Open the footer editor (the shared default for every email)"
            >
              Edit the workspace footer
            </button>{' '}
            once and every email without its own footer follows.
          </p>
        )}
      </div>
    </section>
  );
}

export function EmailEditorPage() {
  const { id } = useParams<{ id: string }>();
  const inspector = useResizablePanel();

  // Keep the inspector overlay state in sync on small screens.
  useEffect(() => {
    document.body.classList.toggle('inspector-open', !inspector.collapsed);
    return () => document.body.classList.remove('inspector-open');
  }, [inspector.collapsed]);
  const navigate = useNavigate();

  const { blocks, componentUsages, setBlocks, setComponentUsages, setPostId } = useEditorStore();
  const { undo, redo, canUndo, canRedo } = useEditorHistory();

  // ── Test-send modal state ───────────────────────────────────────
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [saveComponentOpen, setSaveComponentOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [subject, setSubject] = useState('');

  // ── Fetch email ────────────────────────────────────────────────
  const { data: email, isLoading } = useQuery({
    queryKey: ['email', id],
    queryFn: () => api.emails.get(id!),
    enabled: !!id,
  });

  // ── Shared editor plumbing (fonts, auto-save, shortcuts) ─────
  useEditorFonts();

  const { saveMutation, isDirty } = useEditorAutosave('email', id!);

  const aiComposer = useAiComposer();

  useEditorShortcuts({
    onSave: () => saveMutation.mutate(),
    undo,
    redo,
    onAi: () => aiComposer.openComposer(),
  });

  useEffect(() => {
    if (email && id) {
      setPostId(id);
      const typedPost = email as {
        blocks: string | Block[];
        componentUsages?: string | ComponentUsage[];
        subject?: string;
      };
      const parsed: Block[] =
        typeof typedPost.blocks === 'string' ? JSON.parse(typedPost.blocks) : typedPost.blocks;
      const parsedUsages: ComponentUsage[] =
        typeof typedPost.componentUsages === 'string'
          ? JSON.parse(typedPost.componentUsages || '[]')
          : (typedPost.componentUsages ?? []);
      setBlocks(parsed);
      setComponentUsages(parsedUsages);
      setSubject(typedPost.subject ?? '');
    }
  }, [email, id, setPostId, setBlocks, setComponentUsages]);

  // ── Preview ───────────────────────────────────────────────────
  // Saves the latest state, builds the email and returns its HTML so it
  // can be shown in the device-aware preview overlay.
  const buildPreview = async (): Promise<string> => {
    await api.emails.update(id!, { blocks, componentUsages });
    const res = (await api.emails.build(id!)) as { html?: string };
    if (!res.html) throw new Error('Build returned no HTML');
    return res.html;
  };

  const typedPost = email as Record<string, unknown> | undefined;
  // Preview the email with the design it will actually be built with (the same
  // token cascade and column width the build uses) — see useDocumentDesign.
  const design = useDocumentDesign('email', id);

  // A sent email's footer has already shipped, so the footer panel goes
  // read-only for it (the rest of the page never gated on status).
  const emailStatus = (email as { status?: string } | undefined)?.status;
  const footerReadOnly = emailStatus === 'sent' || emailStatus === 'sending';

  if (isLoading) {
    return <PaneLoading label="Loading email…" />;
  }

  return (
    <div
      className="editor-layout"
      style={{
        gridTemplateColumns: inspector.collapsed ? '1fr auto' : `1fr auto ${inspector.width}px`,
      }}
    >
      {/* ── Canvas side ─────────────────────────────────────── */}
      <div className="flex min-w-0 flex-col overflow-hidden">
        <EditorToolbar
          backTitle="emails"
          onBack={() => navigate('/emails')}
          onOpenInspector={() => inspector.setCollapsed(false)}
          title={{
            value: subject,
            onChange: setSubject,
            onCommit: (value) => {
              void api.emails.update(id!, { subject: value });
            },
            placeholder: 'Email subject…',
          }}
          saveState={isDirty ? 'dirty' : saveMutation.isSuccess ? 'saved' : 'idle'}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={undo}
          onRedo={redo}
          onSave={() => saveMutation.mutate()}
          canSave={isDirty}
          saving={saveMutation.isPending}
          actions={
            <>
              <button
                type="button"
                onClick={() => aiComposer.openComposer()}
                className="btn-ghost rounded-lg px-2.5 py-1.5 text-xs"
                title="Draft with AI (⌘J)"
              >
                <Sparkles size={13} className="text-accent" />
                Draft with AI
              </button>
              <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                className="btn-ghost rounded-lg px-2.5 py-1.5 text-xs"
                title="Preview the rendered email (saves first)"
              >
                <MonitorPlay size={13} />
                Preview
              </button>
              <button
                type="button"
                onClick={() => setSaveComponentOpen(true)}
                disabled={blocks.length === 0}
                className="btn-ghost rounded-lg px-2.5 py-1.5 text-xs disabled:opacity-30"
                title="Save blocks as a reusable component"
              >
                <ComponentIcon size={13} />
                Save as component
              </button>

              {/* Test send */}
              <button
                type="button"
                onClick={() => setIsTestModalOpen(true)}
                className="btn-ghost rounded-lg px-2.5 py-1.5 text-xs"
                title="Build and send a test email via SMTP"
              >
                <Send size={13} /> Test send
              </button>
            </>
          }
        />

        {/* Per-email footer — workspace default, or this email's own content */}
        <EmailFooterPanel emailId={id!} email={email} readOnly={footerReadOnly} />

        {/* Canvas — same styling as the posts editor */}
        <EditorCanvas
          contentKind="email"
          rawVariableDefinitions={
            typedPost?.['variableDefinition'] as Record<string, unknown> | undefined
          }
          design={design}
        />
      </div>

      {/* ── Inspector panel ──────────────────────────────────── */}
      <PanelResizer
        collapsed={inspector.collapsed}
        onResizeStart={inspector.startResize}
        onExpand={() => inspector.setCollapsed(false)}
      />
      {!inspector.collapsed && (
        <>
          {/* Tap-out scrim — only when the inspector overlays the canvas (small screens) */}
          <div
            className="bg-ink/30 fixed inset-0 z-[75] backdrop-blur-[2px] lg:hidden"
            onClick={inspector.toggleCollapsed}
            aria-hidden="true"
          />
          <InspectorPanel
            post={typedPost}
            postId={id!}
            mode="email"
            onToggleCollapse={inspector.toggleCollapsed}
          />
        </>
      )}

      {/* ── Test-send modal ──────────────────────────────────── */}
      {isTestModalOpen && (
        <SendTestEmailModal
          emailId={id!}
          subject={subject}
          blocks={blocks}
          variableDefinition={typedPost?.['variableDefinition']}
          onClose={() => setIsTestModalOpen(false)}
        />
      )}

      {/* ── Save-as-component ────────────────────────────────── */}
      {saveComponentOpen && (
        <SaveComponentModal contentKind="email" onClose={() => setSaveComponentOpen(false)} />
      )}

      {/* ── Rendered preview ─────────────────────────────────── */}
      {previewOpen && (
        <PreviewModal
          kind="email"
          title={subject}
          onBuild={buildPreview}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      {/* ── Draft with AI composer ───────────────────────────── */}
      {aiComposer.open && (
        <AiComposer
          contentKind="email"
          meta={{ subject }}
          onMetaCommit={(patch) => {
            if (patch.subject && patch.subject !== subject) {
              setSubject(patch.subject);
              void api.emails.update(id!, { subject: patch.subject });
            }
          }}
          onClose={() => aiComposer.closeComposer()}
        />
      )}
    </div>
  );
}
